import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Role, ServiceCenter, Useraccount} from "../../../../../types/domain";
import {MantineReactTable, MRT_ColumnDef, useMantineReactTable} from "mantine-react-table";
import {ActionIcon, Alert, Box, Button, Paper, Tooltip} from "@mantine/core";
import {ModalType} from "../../../../../types/UITypes";
import CustomModal from "../modal/CustomModal";
import {IconArchive, IconPencil, IconRestore} from "@tabler/icons-react";
import {MRT_Localization_DE} from "mantine-react-table/locales/de";
import UseraccountService from "../../../../../services/useraccount.service";
import {UserModal} from "../modal/UserModal";
import * as Sentry from "@sentry/react";

type UserData = {
    userRole: Role | null,
    userServiceCenters: Array<ServiceCenter>
}
type UserTableProps = {
    userData: UserData,
    tabValue: string
}

export function UserTable({ userData: { userRole, userServiceCenters }, tabValue }: UserTableProps) {
    const [data, setData] = useState<Useraccount[]>([]);
    const [showAlert, setShowAlert] = useState(false);
    const [showUserModal, setShowUserModal] = useState<boolean>(false);
    const [userToEdit, setUserToEdit] = useState<Useraccount | null>(null);
    const [modal, setModal] = useState<ModalType>({
        show: false,
        title: "",
        body: "",
        decline: false,
        handleOk: (() => {
        }),
        handleCancel: (() => {
        })
    });

    //definition of table columns
    const columns = useMemo<MRT_ColumnDef<Useraccount>[]>(
        () => [
            {
                accessorKey: 'username',
                header: 'Benutzername'
            },
            {
                accessorKey: 'firstname',
                header: 'Vorname'
            },
            {
                accessorKey: 'lastname',
                header: 'Nachname'
            },
            {
                id: 'role',
                header: 'Rolle',
                accessorFn: (row) => row.role?.name
            },
            {
                id: 'serviceCenter',
                header: 'Servicestelle',
                accessorFn: (row) => row.serviceCenters.at(0)?.name
            }
        ], [tabValue]
    );

    const fetchData = useCallback(async () => {
        if (tabValue !== "trainers" || !userRole) return;
        try {
            const response = await UseraccountService.getAll();
            let users = (response?.data || []).filter(u => u.active);

            if (userRole?.name === 'Admin') {
                setData(users.filter(u => u.role?.name !== 'Admin'));
            } else if (userRole?.name === 'DSS Leiter*in') {
                setData(users.filter(u => {
                    return u.role?.name !== 'Admin' &&
                        u.role?.name !== 'DSS Leiter*in' &&
                        u.serviceCenters?.some(sc => userServiceCenters.some(usc => usc.id === sc.id));
                }));
            }
        } catch (e) {
            console.error(e);
        }
    }, [userRole, userServiceCenters, tabValue]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    /**
     * Closes user modal and fetches data
     */
    const handleCloseUserModal = useCallback(async () => {
        setShowUserModal(false);
        setUserToEdit(null);
        await fetchData();
    }, [fetchData]);

    /**
     * Opens a modal to update an useraccount
     * @param user with new data to update
     */
    function updateUser(user: Useraccount) {
        setUserToEdit(user);
        setShowUserModal(true);
    }

    /**
     * Opens a modal to create a new useraccount
     */
    function createUser() {
        setUserToEdit(null);
        setShowUserModal(true);
    }

    /**
     * Sets an useraccount to inactive in the DB
     * @param user to inactivate
     */
    async function inactivateUser(user: Useraccount) {
        setModal({
            ...modal,
            show: true,
            title: "Hinweis",
            body: "Soll der Eintrag wirklich archiviert werden?",
            decline: true,
            handleOk: async () => {
                user.active = false;

                await UseraccountService.update(user, user.id)
                    .catch((e: Error) => {
                        Sentry.captureException(e);
                        setShowAlert(true);
                        return;
                    });

                setModal({...modal, show: false})
                await fetchData();
            },
            handleCancel: () => {
                setModal({...modal, show: false})
                fetchData();
            }
        })
    }

    /**
     * Sets the password of an useraccount to the username
     * @param user to reset the password
     */
    function resetUserPassword(user: Useraccount) {
        setModal({
            ...modal,
            show: true,
            title: "Hinweis",
            body: "Soll das Passwort wirklich zurückgesetzt werden?",
            decline: true,
            handleOk: async () => {
                await UseraccountService.resetPassword(user)
                    .catch((e: Error) => {
                        Sentry.captureException(e);
                        setShowAlert(true);
                        return;
                    });

                setModal({...modal, show: false})
                await fetchData();
            },
            handleCancel: () => {
                setModal({...modal, show: false})
                fetchData();
            }
        })
    }

    const table = useMantineReactTable<Useraccount>({
        columns,
        data,
        initialState: {
            pagination: {
                pageSize: 10,
                pageIndex: 0
            },
            sorting: [
                {
                    id: 'firstname',
                    desc: false
                },
                {
                    id: 'lastname',
                    desc: false
                }
            ]
        },
        enableColumnActions: false,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        positionPagination: "bottom",
        enableRowActions: true,
        positionActionsColumn: "last",
        renderRowActions: ({row}) => {
            return <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
                <Tooltip label="Bearbeiten">
                    <ActionIcon color="blueish" onClick={() => updateUser(row.original)}>
                        <IconPencil/>
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Passwort zurücksetzen">
                    <ActionIcon color="blueish" onClick={() => resetUserPassword(row.original)}>
                        <IconRestore/>
                    </ActionIcon>
                </Tooltip>
                <Tooltip label="Archivieren">
                    <ActionIcon style={{color: "var(--salmon)"}} onClick={() => inactivateUser(row.original)}>
                        <IconArchive/>
                    </ActionIcon>
                </Tooltip>
            </Box>
        },
        localization: MRT_Localization_DE,
        mantinePaginationProps: {
            showRowsPerPage: false
        }
    });
    return (
        <>
            <Paper radius="xs" style={{marginTop: "1rem"}}>
                <MantineReactTable
                    table={table}
                />
                {
                    showAlert ?
                        <Alert id={'userDatabaseAlert'} w={"100%"}
                               title={"Archivieren fehlgeschlagen"}
                               ta={"left"} color="red" mt={"10px"}>
                            Fehler beim Archivieren des Eintrages!
                        </Alert> : <></>
                }
                <div className="buttonRow">
                    <Button onClick={createUser}>
                        Neuer Eintrag
                    </Button>
                </div>
            </Paper>
            {
                showUserModal && (
                    <UserModal
                        opened={showUserModal}
                        close={handleCloseUserModal}
                        title={userToEdit ? "Trainer*in aktualisieren" : "Trainer*in anlegen"}
                        user={userToEdit}
                        userRole={userRole}
                        userServiceCenters={userServiceCenters}
                    />
                )
            }
            <CustomModal title={modal.title}
                         body={modal.body}
                         decline={modal.decline} showModal={modal.show} handleOk={modal.handleOk}
                         handleCancel={modal.handleCancel}
            />
        </>
    );
}